// src/components/customers/JobDetailsCard.jsx
import { useState, useEffect } from "react";
import Swal from "sweetalert2";

// Import child components for modular architecture
import JobSection from "./JobSection";
import JobSpecBadge from "./JobSpecBadge";
import DoorWorkInfo from "./DoorWorkInfo";
import AdditionalInfoCards from "./AdditionalInfoCards";
import JobModal from "./JobModal";

// Import services
import {
  getCustomerById,
  updateCustomer,
} from "../../services/customerService";

/**
 * JobDetailsCard Component
 *
 * Main component for displaying comprehensive job information in a professional card layout.
 * Orchestrates multiple child components to create a complete job overview.
 *
 * @param {Object} props - Component props
 * @param {Object} props.job - Complete job object with all specifications
 * @param {Function} props.onJobUpdate - Callback function when job is updated
 * @param {string} props.customerId - Customer ID for job updates
 */
const JobDetailsCard = ({ job, onJobUpdate, customerId }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Default job template
  const [defaultParams] = useState({
    id: null,
    date: new Date().toLocaleDateString("en-GB"),
    hasUnderlay: false,
    hasGrippers: false,
    floorIsGood: false,
    oldFlooringRemoved: false,
    furnitureRemoval: false,
    concrete: false,
    doorsNeedCutting: 0,
    numberOfDoorPlateNeeded: 0,
    comments: "",
    plan: "",
    products: "",
  });

  // Current job parameters for editing
  const [params, setParams] = useState(
    JSON.parse(JSON.stringify(defaultParams))
  );

  // Defensive programming - handle missing job object
  if (!job) {
    return (
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p>No job information available</p>
        </div>
      </div>
    );
  }

  // Handle edit job
  const handleEditJob = () => {

    // Initialize form with current job data
    if (job) {
      setParams(JSON.parse(JSON.stringify(job)));
    }

    setIsEditModalOpen(true);
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    if (!e.target) {
      if (typeof e === "string") {
        if (e === "") return;
        setParams({ ...params, date: e });
      } else {
        setParams({ ...params, date: e.value });
      }
      return;
    }

    const { value, id, type, checked } = e.target;

    if (type === "number") {
      if (value === "") {
        setParams({ ...params, [id]: 0 });
      } else {
        setParams({ ...params, [id]: Number(value) });
      }
    } else if (type === "checkbox") {
      setParams({ ...params, [id]: checked });
    } else {
      setParams({ ...params, [id]: value });
    }
  };

  // Save job changes
  const saveJob = async () => {
    if (!params.date) {
      showMessage("Please enter a Date", "error");
      return;
    }

    // Debug: Check if we have customerId from props

    if (!customerId) {
      showMessage("Customer ID not found. Please refresh the page.", "error");
      return;
    }

    try {
      // Get current customer data to update the job
      const customerData = await getCustomerById(customerId);

      if (!customerData) {
        showMessage("Customer not found", "error");
        return;
      }


      // Update the specific job in the jobs array
      const updatedJobs = customerData.jobs.map((j) =>
        j.id === params.id ? params : j
      );


      // Update customer with modified jobs
      await updateCustomer(customerId, {
        ...customerData,
        jobs: updatedJobs,
      });

      // Call the parent update callback
      if (onJobUpdate) {
        onJobUpdate(params);
      }

      setIsEditModalOpen(false);
      showMessage("Job updated successfully", "success");
    } catch (error) {
      console.error("Error updating job:", error);
      showMessage("Failed to update job. Please try again.", "error");
    }
  };

  // Show toast message
  const showMessage = (msg = "", type = "success") => {
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { container: "toast" },
    }).fire({
      icon: type,
      title: msg,
      padding: "10px 20px",
    });
  };

  // Handle job update
  const handleJobUpdate = (updatedJob) => {
    if (onJobUpdate) {
      onJobUpdate(updatedJob);
    }
    setIsEditModalOpen(false);
  };

  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* ============================================
          CARD HEADER - Job Overview and Edit Button
          ============================================ */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <div className="flex justify-end items-end mb-4">
          {/* Edit Job Button - Top Right Corner */}
          <button
            onClick={handleEditJob}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
            title="Edit job details and specifications"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit Job
          </button>
        </div>
        <div className="flex items-center justify-between">
          {/* Job Title and Date */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              {/* Job clipboard icon for visual context */}
              <svg
                className="w-6 h-6 mr-2 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              Job Details & Specifications
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Scheduled for:{" "}
              <span className="font-medium">{job.date || "Not specified"}</span>
            </p>
          </div>

          {/* Job ID */}
          <div className="flex items-center gap-3">
            {/* Job ID reference (last 8 characters for brevity) */}
            {job.id && (
              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded border">
                ID: {job.id.slice(-8).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============================================
          CARD BODY - Detailed Job Information
          ============================================ */}
      <div className="p-6">
        {/* Floor Preparation Section */}
        <JobSection
          title="Floor Preparation"
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
          collapsible={false}
        >
          {/* Responsive grid for floor preparation badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <JobSpecBadge
              label="Underlay"
              value={job.hasUnderlay}
              type="info"
              trueText="Has Underlay"
              falseText="No Underlay"
              showWhenFalse={true}
            />
            <JobSpecBadge
              label="Grippers"
              value={job.hasGrippers}
              type="info"
              trueText="Has Grippers"
              falseText="No Grippers"
              showWhenFalse={true}
            />
            <JobSpecBadge
              label="Floor Condition"
              value={job.floorIsGood}
              type={job.floorIsGood ? "success" : "warning"}
              trueText="Floor is Good"
              falseText="Floor Needs Work"
              showWhenFalse={true}
            />
            <JobSpecBadge
              label="Old Flooring"
              value={job.oldFlooringRemoved}
              type="info"
              trueText="Old Flooring Removed"
              falseText="Old Flooring Present"
              showWhenFalse={true}
            />
          </div>
        </JobSection>

        {/* Work Requirements Section */}
        <JobSection
          title="Work Requirements"
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          }
          collapsible={false}
          className="mt-6"
        >
          <div className="space-y-4">
            {/* General work requirement badges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <JobSpecBadge
                label="Furniture Removal"
                value={job.furnitureRemoval}
                type="warning"
                trueText="Furniture Removal Required"
                showWhenFalse={false}
              />
              <JobSpecBadge
                label="Concrete Work"
                value={job.concrete}
                type="info"
                trueText="Concrete Work Required"
                showWhenFalse={false}
              />
            </div>

            {/* Specialized door work component */}
            <DoorWorkInfo job={job} />
          </div>
        </JobSection>

        {/* Additional Information Section - Only show if data exists */}
        {(job.plan || job.products || job.comments) && (
          <div className="mt-6">
            <AdditionalInfoCards job={job} />
          </div>
        )}
      </div>

      {/* Edit Job Modal */}

      <JobModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
        }}
        params={params}
        onInputChange={handleInputChange}
        onSave={saveJob}
      />
    </div>
  );
};

export default JobDetailsCard;
